import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import BrandColorPicker from "../common/BrandColorPicker";
import Button from "./Button";
import { saveBrandProfile, DEFAULT_BRAND_COLORS } from "../../utils/brandColors";
import { createTheme, toApiColors } from "../../utils/brandApi";
import { refreshThemes } from "../../utils/useThemes";
import useUserEmail from "../../utils/useUserEmail";
import useUserPlan from "../../utils/useUserPlan";
import enFlag from "../../assets/sideBar/english.svg";
import esFlag from "../../assets/sideBar/spanish.svg";
import frFlag from "../../assets/sideBar/french.svg";
import itFlag from "../../assets/sideBar/italian.svg";

const WelcomeModal = ({ onStart }) => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [language, setLanguage] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [brandColors, setBrandColors] = useState(DEFAULT_BRAND_COLORS);
  const [themeName, setThemeName] = useState("");
  const [themeError, setThemeError] = useState("");
  const [saving, setSaving] = useState(false);
  const userEmail = useUserEmail();
  const userPlan = useUserPlan();

  const languages = [
    { code: "en", label: "English", flag: enFlag },
    { code: "es", label: "Spanish", flag: esFlag },
    { code: "fr", label: "French", flag: frFlag },
    { code: "it", label: "Italian", flag: itFlag },
  ];

  const handleLanguageChange = (code) => {
    setLanguage(code);
    i18n.changeLanguage(code);
  };

  const handleSaveAndStart = async () => {
    saveBrandProfile({ firstName, lastName, brandColors });

    const touchedColors =
      JSON.stringify(brandColors) !== JSON.stringify(DEFAULT_BRAND_COLORS);

    // No name: fine if they left the colours alone, but if they picked
    // colours we'd be silently dropping them.
    if (!themeName.trim()) {
      if (touchedColors) { setThemeError(t("welcomeModal.themeNameRequired")); return; }
      onStart();
      return;
    }

    setSaving(true);
    setThemeError("");
    try {
      await createTheme(userEmail, userPlan, {
        name: themeName.trim(),
        ...toApiColors(brandColors),
      });
      await refreshThemes(userEmail);
      onStart();
    } catch (err) {
      console.error("Welcome theme save failed:", err);
      setThemeError(t("welcomeModal.themeSaveError"));
      setSaving(false);
    }
  };

  const handleSkip = () => {
    onStart();
  };

  return (
    <div className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-[640px] max-h-[90vh] flex flex-col border-2 border-black-800">

        {/* ── Step 1 — Language ── */}
        {step === 1 && (
          <>
            <div className="p-4">
              <h2 className="text-lg font-bold mb-4">
                {t("welcomeModal.title")}
              </h2>

              <div className="bg-link-100 text-black-800 text-lg px-2 mb-4">
                {t("welcomeModal.description")}
              </div>

              <p className="text-lg font-bold mb-4">
                {t("welcomeModal.language")}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-3 mb-1">
                {languages.map((lang) => {
                  const isSelected = language === lang.code;
                  return (
                    <Button
                      key={lang.code}
                      variant={isSelected ? "languageActive" : "language"}
                      onClick={() => handleLanguageChange(lang.code)}
                      className="w-full"
                    >
                      <img src={lang.flag} alt="" className="w-6 h-6 object-contain" />
                      {lang.label}
                    </Button>
                  );
                })}
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  variant="dark"
                  onClick={() => setStep(2)}
                  disabled={!language}
                  className="w-full md:w-[300px]"
                >
                  {t("buttons.next")}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* ── Step 2 — Brand profile (optional) ── */}
        {step === 2 && (
          <>
            {/* Fixed header */}
            <div className="p-4 pb-0">
              <h2 className="text-lg font-bold mb-2">
                {t("welcomeModal.step2Title")}
              </h2>
              <div className="bg-link-100 text-black-800 text-lg px-2 mb-4">
                {t("welcomeModal.step2Description")}
              </div>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-4">
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="flex-1">
                  <label className="text-sm sm:text-lg font-bold block mb-1">
                    {t("welcomeModal.firstName")}
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t("welcomeModal.firstNamePlaceholder")}
                    className="border rounded px-4 py-2.5 w-full outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-sm sm:text-lg font-bold block mb-1">
                    {t("welcomeModal.lastName")}
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t("welcomeModal.lastNamePlaceholder")}
                    className="border rounded px-4 py-2.5 w-full outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
              </div>

              <p className="text-sm sm:text-lg font-bold block mb-1">
                {t("welcomeModal.themeName")}
              </p>
              <input
                type="text"
                value={themeName}
                onChange={(e) => { setThemeName(e.target.value); setThemeError(""); }}
                placeholder={t("welcomeModal.themeNamePlaceholder")}
                maxLength={40}
                className="border rounded px-4 py-2.5 w-full outline-none focus:ring-2 focus:ring-primary-300 mb-4"
              />

              <p className="text-sm sm:text-lg font-bold block mb-1">
                {t("welcomeModal.brandColorsTitle")}
              </p>
              <BrandColorPicker colors={brandColors} onChange={setBrandColors} />

              {themeError && (
                <p className="text-sm text-error-500 mt-3">{themeError}</p>
              )}

              <p className="text-sm text-black-400 mt-3 mb-4">
                {t("welcomeModal.step2Hint")}
              </p>
            </div>

            {/* Fixed footer */}
            <div className="p-4 pt-3 border-t border-gray-100 flex flex-col md:flex-row gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="w-full md:w-auto"
              >
                {t("buttons.skip")}
              </Button>
              <Button
                variant="dark"
                onClick={handleSaveAndStart}
                disabled={saving}
                className="w-full md:w-[300px]"
              >
                {t("welcomeModal.saveAndStart")}
              </Button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default WelcomeModal;